/* PETQuarters: Selly (Treedify) sale-offer reader for collection cards.
 * Selly publishes its live offer configuration to every page through window.tdfEventHub ('data_app2') — the same
 * data its own "NN% OFF" label and PDP sale widget are drawn from. This module only READS that payload and exposes
 * the per-variant promo price so cards can show the same sale price the PDP shows. Conservative by design:
 *   - only "Sale" offers (type 1) with a plain percentage discount (discount.type_id 1), a fixed amount off per unit
 *     (discount.type_id 2, e.g. "$20 OFF") or a fixed per-unit sale price (discount.type_id 7, e.g. "$5.99") are priced —
 *     all three are per-unit prices the PDP widget shows verbatim;
 *   - bundle / BOGO / quantity offers are never priced (their price is not a per-unit price);
 *   - the offer must be inside its schedule window right now; an offer with no schedule at all is always-on (that is how
 *     Selly treats it — its own label and PDP price show); offers with weekly/monthly windows are skipped;
 *   - product groups: type 3 = whole product, type 2 = product with optional per-variant list (pvids);
 *   - promo cents = floor(regular cents x (100 - pct) / 100), matching Selly's own rounding ($74.99 @ 50% -> $37.49).
 * Round L — label follows the selected size. Selly injects ONE product-level label (.tdf_label) into each card image, so a
 * "$20 OFF for 2kg Bag!" label sat on the card even with the 450g selected. labelFor() answers "does ANY active Selly offer
 * (any type, so bundles/BOGO included) reference this exact variant?" and syncLabel() hides/shows Selly's own element
 * accordingly, and swaps its text to the matching offer's label when the product sits in more than one offer. Selly re-draws
 * its labels on its own schedule (load-more etc.), so a MutationObserver re-applies the last decision to any re-inserted label.
 * Unknown states (Selly absent/late, product not in any offer) leave Selly's element exactly as it was.
 * No network calls of its own, nothing written anywhere. If Selly is absent or late, cards simply keep the regular price. */
(() => {
  'use strict';
  if (window.PQSelly) return;
  const byProduct = new Map(); // productId -> [{pct,fixed,off,label,variants:Set|null}]  (priced Sale offers only)
  const labelByProduct = new Map(); // productId -> [{label,priced,variants:Set|null}]   (every active offer with a label)
  let ready = false, raw = null;
  function now() { return Math.floor(Date.now() / 1000); }
  function scheduleActive(d, ids) {
    if (!Array.isArray(ids) || !ids.length) return true; // no schedule = always on (Selly shows these offers)
    const t = now();
    return ids.some(id => {
      const s = d.schedules && d.schedules[id]; if (!s) return false;
      if ((s.w && s.w.d != null) || (s.m && s.m.d != null)) return false;
      const st = Number(s.st && s.st.s) || 0, et = Number(s.et && s.et.s) || 0;
      return st <= t && (et === 0 || t < et);
    });
  }
  function groupsOf(o) {
    const list = [];
    for (const g of o.product_groups || []) {
      const type = Number(g.type_id); if (type !== 2 && type !== 3) continue;
      for (const pid of g.ids || []) {
        const key = String(pid);
        const pv = type === 2 && g.pvids && g.pvids[key];
        list.push({ key, variants: Array.isArray(pv) && pv.length ? new Set(pv.map(String)) : null });
      }
    }
    return list;
  }
  function index(d) {
    byProduct.clear(); labelByProduct.clear();
    const offers = d && d.offers; if (!offers) return;
    for (const typeKey of Object.keys(offers)) {
      const isSale = typeKey === '1';
      for (const o of Object.values(offers[typeKey] || {})) {
        if (!o || !scheduleActive(d, o.trigger && o.trigger.schedule_ids)) continue;
        const label = String(o.label || (o.trigger && o.trigger.label) || o.message || '').trim();
        const disc = o.discount, dtype = disc ? Number(disc.type_id) : 0;
        let pct = 0, fixed = 0, off = 0, priced = false;
        if (isSale && disc) {
          if (dtype === 1) { pct = Number(disc.value); priced = pct > 0 && pct < 100; }
          else if (dtype === 2) { off = Math.round(Number(disc.value) * 100); priced = off > 0; }
          else if (dtype === 7) { fixed = Math.round(Number(disc.value) * 100); priced = fixed > 0; }
        }
        for (const g of groupsOf(o)) {
          if (priced) {
            if (!byProduct.has(g.key)) byProduct.set(g.key, []);
            byProduct.get(g.key).push({ pct, fixed, off, label, variants: g.variants });
          }
          if (label) {
            if (!labelByProduct.has(g.key)) labelByProduct.set(g.key, []);
            labelByProduct.get(g.key).push({ label, priced, variants: g.variants });
          }
        }
      }
    }
  }
  function offerFor(productId, variantId, regularCents) {
    if (!ready) return null;
    const list = byProduct.get(String(productId)); if (!list) return null;
    const cents = Number(regularCents); if (!(Number.isFinite(cents) && cents > 0)) return null;
    let best = null, bestCents = 0;
    for (const e of list) {
      if (e.variants && !e.variants.has(String(variantId))) continue;
      const promo = e.fixed ? e.fixed : e.off ? cents - e.off : Math.floor(cents * (100 - e.pct) / 100); // lowest resulting price wins
      if (!(promo > 0 && promo < cents)) continue;
      if (!best || promo < bestCents) { best = e; bestCents = promo; }
    }
    if (!best) return null;
    const pct = best.pct || Math.round((1 - bestCents / cents) * 100);
    return { cents: bestCents, pct, label: best.label || (best.pct ? best.pct + '% OFF' : 'SALE') };
  }
  // undefined = unknown (not ready / product in no offer): leave Selly's label alone. null = product has offers but none
  // cover this variant: hide. {label} = this variant is covered: show (priced offers first, so the label matches the price).
  function labelFor(productId, variantId) {
    if (!ready) return undefined;
    const list = labelByProduct.get(String(productId)); if (!list) return undefined;
    const vid = String(variantId);
    const hits = list.filter(e => !e.variants || e.variants.has(vid));
    if (!hits.length) return null;
    return { label: (hits.find(e => e.priced) || hits[0]).label };
  }
  const LABEL_SEL = '.tdf_label,.tdf_ribbon';
  function applyLabel(media) {
    const pid = media.dataset.pqSellyPid, vid = media.dataset.pqSellyVid; if (!pid || !vid) return;
    const state = labelFor(pid, vid); if (state === undefined) return;
    media.querySelectorAll(LABEL_SEL).forEach(el => {
      el.hidden = state === null;
      if (state) { const span = el.querySelector('.tdf_ribbon_span'); if (span && state.label && span.textContent.trim() !== state.label) span.textContent = state.label; }
    });
  }
  // Cards call this with their media element whenever the selected variant changes (and on pq:selly-ready).
  function syncLabel(media, productId, variantId) {
    if (!media || productId == null || variantId == null) return;
    media.dataset.pqSellyPid = String(productId); media.dataset.pqSellyVid = String(variantId);
    applyLabel(media);
  }
  // Selly removes and re-inserts every .tdf_label when it re-runs addLabel; re-apply the card's last decision before paint.
  function watchLabels() {
    if (!window.MutationObserver || !document.body) return;
    new MutationObserver(records => {
      const medias = new Set();
      for (const r of records) for (const n of r.addedNodes) {
        if (n.nodeType !== 1) continue;
        const el = n.matches(LABEL_SEL) ? n : n.querySelector(LABEL_SEL); if (!el) continue;
        const media = el.closest('[data-pq-selly-pid]'); if (media) medias.add(media);
      }
      medias.forEach(applyLabel);
    }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.body) watchLabels(); else document.addEventListener('DOMContentLoaded', watchLabels, { once: true });
  function onData(_name, d) {
    try { raw = d; index(d); ready = true; } catch (e) { ready = false; return; }
    document.dispatchEvent(new CustomEvent('pq:selly-ready'));
  }
  let tries = 0;
  (function hook() {
    const hub = window.tdfEventHub;
    if (hub && typeof hub.ListenToEvent == 'function') { try { hub.ListenToEvent('data_app2', onData, true); } catch (e) { /* no Selly */ } return; }
    if (++tries < 80) setTimeout(hook, 250); // up to ~20s for Selly's loader; otherwise cards keep regular prices
  })();
  // Re-evaluate schedule windows once a minute so an offer that starts/ends while the page is open updates in place.
  setInterval(() => { if (raw) { index(raw); document.dispatchEvent(new CustomEvent('pq:selly-ready')); } }, 60000);
  window.PQSelly = { offerFor, labelFor, syncLabel, isReady: () => ready };
})();
